import { EditableText } from '@/components/editor/EditableText';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import { renderMarkdownLite } from '@/lib/markdown';
import type { Fiche, FicheColor, FicheSection } from '@pharmafiche/schema';
import { Fragment } from 'react';
import type { FicheEditHandlers } from './FicheDessinee';

interface FicheInfographiqueProps {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}

/**
 * Phase 6 — magazine-style infographique template.
 *
 * Pale tinted section backgrounds (#fff5ec, #eef7f0, #f3edf9…). Fraunces for
 * display, Outfit for caps labels and body. Any numeric fragment gets the
 * "big number" treatment. Sticky-note mnemonic. Pull-quote bullets when
 * emphasis is set.
 */
export function FicheInfographique({ fiche, editHandlers }: FicheInfographiqueProps) {
  return (
    <div
      className="min-h-screen bg-white text-ink"
      style={{
        WebkitFontSmoothing: 'antialiased',
        fontFamily: 'Outfit, sans-serif',
      }}
    >
      <div className="max-w-[1000px] mx-auto px-6 md:px-10 pt-10 pb-12">
        <header className="mb-10">
          <div className="font-outfit uppercase tracking-[0.22em] text-[12px] text-ink-soft mb-2">
            Fiche n° {fiche.id.slice(0, 4)} · Pharmacologie
          </div>
          <h1
            className="font-fraunces font-bold leading-[1.02]"
            style={{ fontSize: 'clamp(40px, 6vw, 64px)', letterSpacing: '-0.01em' }}
          >
            <EditableText
              value={fiche.meta.title}
              plain
              ariaLabel="Titre"
              onCommit={(t) => editHandlers?.updateMetaTitle?.(t)}
            />
          </h1>
          {fiche.meta.subtitle ? (
            <p className="font-outfit text-[16px] text-ink-soft mt-2 max-w-[640px]">
              {fiche.meta.subtitle}
            </p>
          ) : null}
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {fiche.sections.map((sec, i) => (
            <SectionRenderer key={sec.id} section={sec} num={i + 1} editHandlers={editHandlers} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** A few pastel section backgrounds rotated by color. */
const TINTS: Record<FicheColor, string> = {
  blue: '#eaf2fb',
  green: '#eaf6ee',
  purple: '#f3edf9',
  orange: '#fff1e3',
  red: '#fdecec',
  yellow: '#fff8d6',
  neutral: '#f6f6f4',
};

function tintFor(section: FicheSection): string {
  if (section.type === 'drug_card') return TINTS[section.drug.color];
  if (section.type === 'intro' || section.type === 'classification') {
    return TINTS[section.color];
  }
  if (section.type === 'warning') return TINTS.red;
  if (section.type === 'clinical_case') return TINTS.red;
  if (section.type === 'mnemonic') return TINTS.yellow;
  return TINTS.neutral;
}

function accentFor(section: FicheSection): string {
  if (section.type === 'drug_card') return FICHE_COLOR_HEX[section.drug.color];
  if (section.type === 'intro' || section.type === 'classification') {
    return FICHE_COLOR_HEX[section.color];
  }
  if (section.type === 'warning' || section.type === 'clinical_case') return FICHE_COLOR_HEX.red;
  if (section.type === 'mnemonic') return FICHE_COLOR_HEX.blue;
  return FICHE_COLOR_HEX.neutral;
}

function SectionRenderer({
  section,
  num,
  editHandlers,
}: {
  section: FicheSection;
  num: number;
  editHandlers?: FicheEditHandlers;
}) {
  const accent = accentFor(section);
  const tint = tintFor(section);
  const isWide = section.type === 'diagram' || section.type === 'comparison_table';

  return (
    <article
      data-section-id={section.id}
      className={['rounded-[20px] p-6 md:p-7', isWide ? 'md:col-span-2' : ''].join(' ')}
      style={{
        background: tint,
        border: `1px solid ${accent}33`,
      }}
    >
      <header className="mb-3 flex items-baseline gap-2">
        <span
          className="font-outfit uppercase tracking-[0.22em] text-[11px]"
          style={{ color: accent }}
        >
          #{String(num).padStart(2, '0')}
        </span>
        <h2 className="font-fraunces font-bold text-[24px] leading-tight" style={{ color: accent }}>
          {renderHeading(section, editHandlers)}
        </h2>
      </header>

      {renderBody(section, accent, editHandlers)}
    </article>
  );
}

function renderHeading(section: FicheSection, editHandlers?: FicheEditHandlers) {
  if (
    section.type === 'intro' ||
    section.type === 'classification' ||
    section.type === 'comparison_table'
  ) {
    return (
      <EditableText
        value={section.title}
        plain
        ariaLabel="Titre"
        onCommit={(t) => editHandlers?.updateSectionTitle?.(section.id, t)}
      />
    );
  }
  if (section.type === 'drug_card') {
    return (
      <EditableText
        value={section.drug.name}
        plain
        ariaLabel="Nom de la molécule"
        onCommit={(t) => editHandlers?.updateDrugName?.(section.id, t)}
      />
    );
  }
  if (section.type === 'clinical_case') return 'Cas clinique';
  if (section.type === 'mnemonic') return 'Mnémo';
  if (section.type === 'warning') return 'Avertissement';
  if (section.type === 'diagram') return 'Mécanisme';
  if (section.type === 'free_text') return section.title ?? 'Note';
  return null;
}

function renderBody(section: FicheSection, accent: string, editHandlers?: FicheEditHandlers) {
  if (section.type === 'intro' || section.type === 'classification') {
    return (
      <>
        <ul className="m-0 mt-1 pl-0 list-none">
          {section.bullets.map((b) => (
            <BulletRow
              key={b.id}
              bulletId={b.id}
              text={b.text}
              accent={b.emphasis ? FICHE_COLOR_HEX[b.emphasis] : accent}
              emphasis={!!b.emphasis}
              onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
            />
          ))}
        </ul>
        {section.type === 'intro' && section.warning ? (
          <p className="font-outfit text-[14px] mt-3" style={{ color: accent }}>
            ⚠️{' '}
            <EditableText
              value={section.warning}
              ariaLabel="Avertissement"
              onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
            />
          </p>
        ) : null}
        {section.type === 'classification' && section.note ? (
          <p
            className="font-fraunces italic text-[16px] mt-3 pl-3"
            style={{ borderLeft: `4px solid ${accent}` }}
          >
            <EditableText
              value={section.note}
              ariaLabel="Note"
              onCommit={(t) => editHandlers?.updateSectionWarning?.(section.id, t)}
            />
          </p>
        ) : null}
      </>
    );
  }

  if (section.type === 'drug_card') {
    return (
      <>
        <ul className="m-0 mt-1 pl-0 list-none">
          {section.drug.intro_bullets.map((b) => (
            <BulletRow
              key={b.id}
              bulletId={b.id}
              text={b.text}
              accent={accent}
              onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
            />
          ))}
        </ul>
        {section.drug.sub_sections.map((ss) => {
          // If the sub-section has a single numeric bullet, render it as a BigNumber tile
          const onlyNumeric = ss.bullets.length === 1 && /\d/.test(ss.bullets[0]?.text ?? '');
          return (
            <Fragment key={ss.id}>
              <div
                className="font-outfit uppercase tracking-[0.2em] text-[10px] mt-4 mb-1"
                style={{ color: accent }}
              >
                <EditableText
                  value={ss.label}
                  plain
                  ariaLabel="Étiquette"
                  onCommit={(t) => editHandlers?.updateDrugSubsectionLabel?.(section.id, ss.id, t)}
                />
              </div>
              {onlyNumeric ? (
                <BigNumberRow
                  bullets={ss.bullets}
                  accent={accent}
                  onCommit={(id, t) =>
                    editHandlers?.updateDrugSubsectionBulletText?.(section.id, ss.id, id, t)
                  }
                />
              ) : (
                <ul className="m-0 pl-0 list-none">
                  {ss.bullets.map((b) => (
                    <BulletRow
                      key={b.id}
                      bulletId={b.id}
                      text={b.text}
                      accent={ss.color ? FICHE_COLOR_HEX[ss.color] : accent}
                      onCommit={(t) =>
                        editHandlers?.updateDrugSubsectionBulletText?.(section.id, ss.id, b.id, t)
                      }
                    />
                  ))}
                </ul>
              )}
            </Fragment>
          );
        })}
      </>
    );
  }

  if (section.type === 'clinical_case') {
    return (
      <>
        <p className="font-outfit text-[15px] leading-[1.55]">
          <EditableText
            value={section.patient}
            ariaLabel="Patient"
            onCommit={(t) => editHandlers?.updateClinicalPatient?.(section.id, t)}
          />
        </p>
        <ul className="m-0 mt-2 pl-4 list-disc text-[14px]">
          {section.prescription.map((p, i) => (
            <li key={`${section.id}-rx-${i}`}>{renderMarkdownLite(p)}</li>
          ))}
        </ul>
        <p className="font-fraunces italic text-[18px] mt-2" style={{ color: accent }}>
          «{' '}
          <EditableText
            value={section.question}
            ariaLabel="Question"
            onCommit={(t) => editHandlers?.updateClinicalQuestion?.(section.id, t)}
          />
          {' »'}
        </p>
        <div
          className="font-outfit uppercase tracking-[0.2em] text-[10px] mt-3 mb-1"
          style={{ color: accent }}
        >
          Conduite à tenir
        </div>
        <ul className="m-0 pl-0 list-none">
          {section.conduite.map((b) => (
            <BulletRow
              key={b.id}
              bulletId={b.id}
              text={b.text}
              accent={accent}
              onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
            />
          ))}
        </ul>
      </>
    );
  }

  if (section.type === 'mnemonic') {
    return (
      <div
        className="rounded-control p-3 inline-block font-outfit text-[18px] leading-[1.4]"
        style={{
          background: '#fff3a8',
          transform: 'rotate(-1.5deg)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
        }}
      >
        <EditableText
          value={section.text}
          boldColor={accent}
          ariaLabel="Mnémo"
          onCommit={(t) => editHandlers?.updateMnemonicText?.(section.id, t)}
        />
      </div>
    );
  }

  if (section.type === 'warning') {
    return (
      <p
        className="font-fraunces italic text-[20px] leading-[1.45] pl-4"
        style={{ borderLeft: `4px solid ${accent}`, color: accent }}
      >
        <EditableText
          value={section.text}
          ariaLabel="Avertissement"
          onCommit={(t) => editHandlers?.updateWarningText?.(section.id, t)}
        />
      </p>
    );
  }

  if (section.type === 'diagram') {
    return (
      <p className="font-outfit text-[14px] text-ink-soft italic">
        Mécanisme — voir la version « Flowchart » de cette fiche.
      </p>
    );
  }

  if (section.type === 'comparison_table') {
    return (
      <table
        className="w-full text-[14px] border-collapse"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <thead>
          <tr>
            <th className="font-outfit uppercase tracking-[0.15em] text-[10px] text-ink-soft text-left p-2">
              &nbsp;
            </th>
            {section.columns.map((c) => (
              <th key={c} className="font-fraunces text-[16px] font-bold text-left p-2">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((r) => (
            <tr key={r.label} className="border-t border-ink/15">
              <td className="font-outfit uppercase tracking-[0.15em] text-[10px] text-ink-soft p-2">
                {r.label}
              </td>
              {r.cells.map((c, j) => (
                <td key={`${r.label}-${j}`} className="font-outfit p-2 align-top">
                  {renderMarkdownLite(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (section.type === 'free_text') {
    return (
      <p className="font-outfit text-[15px] leading-[1.55] whitespace-pre-wrap">{section.body}</p>
    );
  }

  return null;
}

function BulletRow({
  bulletId,
  text,
  accent,
  emphasis,
  onCommit,
}: {
  bulletId: string;
  text: string;
  accent: string;
  emphasis?: boolean;
  onCommit: (next: string) => void;
}) {
  if (emphasis) {
    return (
      <li
        className="font-fraunces italic text-[18px] leading-[1.4] pl-3 my-2"
        style={{ borderLeft: `4px solid ${accent}` }}
        data-bullet-id={bulletId}
      >
        <EditableText value={text} boldColor={accent} ariaLabel="Bullet" onCommit={onCommit} />
      </li>
    );
  }
  return (
    <li
      className="font-outfit text-[14.5px] leading-[1.55] flex items-start gap-2 my-[3px]"
      data-bullet-id={bulletId}
    >
      <span aria-hidden style={{ color: accent, fontWeight: 700 }}>
        •
      </span>
      <span>
        <EditableText value={text} boldColor={accent} ariaLabel="Bullet" onCommit={onCommit} />
      </span>
    </li>
  );
}

function BigNumberRow({
  bullets,
  accent,
  onCommit,
}: {
  bullets: { id: string; text: string }[];
  accent: string;
  onCommit: (id: string, t: string) => void;
}) {
  const first = bullets[0];
  if (!first) return null;
  const numMatch = first.text.match(/([0-9][\d.,\s/×x-]*[0-9]*)\s*([^\s,.]*)/);
  if (!numMatch) {
    return (
      <p className="font-outfit text-[14.5px]">
        <EditableText
          value={first.text}
          ariaLabel="Bullet"
          onCommit={(t) => onCommit(first.id, t)}
        />
      </p>
    );
  }
  const value = (numMatch[1] ?? '').trim();
  const unit = (numMatch[2] ?? '').trim();
  const rest = first.text.replace(numMatch[0], '').trim();
  return (
    <div className="flex items-baseline gap-3 mt-1" data-bullet-id={first.id}>
      <span
        className="font-fraunces font-bold leading-none"
        style={{ fontSize: 56, color: accent }}
      >
        {value}
      </span>
      <span className="font-outfit uppercase tracking-[0.2em] text-[11px] text-ink-soft">
        {unit}
      </span>
      {rest ? (
        <span className="font-outfit text-[14px] text-ink-soft">
          <EditableText
            value={first.text}
            ariaLabel="Bullet"
            onCommit={(t) => onCommit(first.id, t)}
          />
        </span>
      ) : null}
    </div>
  );
}
